import React, { useEffect, useState } from 'react';
import { api, API } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { Card, CardSm, Pill } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { syncQueue, subscribeSync } from '@/lib/syncQueue';
import { CloudArrowUp, CheckCircle, Warning, Download, FileCsv, Code } from '@phosphor-icons/react';

export default function SyncCenter() {
  const [queue, setQueue] = useState(syncQueue.list());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [projects, setProjects] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const unsub = subscribeSync(() => {
      setQueue(syncQueue.list());
      setOnline(navigator.onLine);
    });
    api.get('/projects').then((r) => setProjects(r.data));
    return unsub;
  }, []);

  const syncAll = async () => {
    setBusy(true); setResults(null);
    const trees = queue.filter((q) => q.kind === 'tree').map((q) => q.payload);
    const meas = queue.filter((q) => q.kind === 'measurement').map((q) => q.payload);
    try {
      const { data } = await api.post('/sync/batch', { trees, measurements: meas });
      syncQueue.clear();
      setResults(data);
    } catch (ex) {
      setResults({ error: ex?.response?.data?.detail || 'Sync failed' });
    } finally { setBusy(false); }
  };

  const exportDmrv = async (pid) => {
    const { data } = await api.get(`/export/dmrv/${pid}`);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    triggerDownload(blob, `dmrv-${pid}.json`);
  };

  const exportCsv = async (pid) => {
    const token = localStorage.getItem('mg_token');
    const res = await fetch(`${API}/export/csv/${pid}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const text = await res.text();
    const blob = new Blob([text], { type: 'text/csv' });
    triggerDownload(blob, `measurements-${pid}.csv`);
  };

  return (
    <div data-testid="sync-center">
      <TopBar title="Sync & Export" />
      <div className="p-4 space-y-4">
        <Card data-testid="sync-status-card">
          <div className="flex items-center justify-between mb-2">
            <span className="font-display font-bold uppercase tracking-widest text-xs">Connection</span>
            <Pill color={online ? '#059669' : '#A44A3F'}>{online ? 'Online' : 'Offline'}</Pill>
          </div>
          <p className="mono text-sm text-carbon-soft">
            Records queued: <span className="font-bold text-carbon">{queue.length}</span>
          </p>
          <Button onClick={syncAll} disabled={busy || queue.length === 0 || !online}
                  variant="dark" className="w-full mt-3" data-testid="sync-all-btn">
            <CloudArrowUp size={20} weight="bold" />
            {busy ? 'Syncing…' : 'Sync all'}
          </Button>
        </Card>

        {results && (
          <CardSm data-testid="sync-results">
            <div className="flex items-center gap-2 mb-1">
              {results.error
                ? <Warning size={18} weight="bold" className="text-status-severe" />
                : <CheckCircle size={18} weight="bold" className="text-status-healthy" />}
              <span className="font-display font-bold uppercase tracking-widest text-xs">
                {results.error ? 'Sync error' : 'Sync complete'}
              </span>
            </div>
            <p className="text-sm mono">
              {results.error ?? `${(results.trees||[]).length} trees · ${(results.measurements||[]).length} events`}
            </p>
          </CardSm>
        )}

        {queue.length > 0 ? (
          <ul className="space-y-2" data-testid="sync-queue-list">
            {queue.map((q) => (
              <li key={q.id} className="card-hard-sm p-3 flex items-center justify-between">
                <div>
                  <p className="font-display text-xs font-bold uppercase tracking-widest">{q.kind}</p>
                  <p className="mono text-xs text-carbon-soft">{new Date(q.created_at).toLocaleString()}</p>
                </div>
                <Pill color="#0284C7">pending</Pill>
              </li>
            ))}
          </ul>
        ) : (
          <CardSm><p className="text-sm text-carbon-soft">Queue empty. All records sync immediately when online.</p></CardSm>
        )}

        {/* Exports */}
        <Card data-testid="export-card">
          <div className="flex items-center gap-2 mb-3">
            <Download size={18} weight="bold" />
            <span className="font-display font-bold uppercase tracking-widest text-xs">Exports</span>
          </div>
          {projects.length === 0 ? (
            <p className="text-sm text-carbon-soft">No projects to export.</p>
          ) : (
            <ul className="space-y-2">
              {projects.map((p) => (
                <li key={p.id} className="border-2 border-carbon p-2">
                  <p className="font-display font-bold text-sm mb-2">{p.project_name}</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => exportCsv(p.id)} variant="outline" data-testid={`export-csv-${p.id}`}>
                      <FileCsv size={16} weight="bold" /> CSV
                    </Button>
                    <Button size="sm" onClick={() => exportDmrv(p.id)} data-testid={`export-dmrv-${p.id}`}>
                      <Code size={16} weight="bold" /> dMRV JSON
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function triggerDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
